import { useState, useEffect, useMemo, useCallback } from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Box,
    Button,
    Typography,
    TextField,
    Checkbox,
    FormControlLabel,
    Switch,
    IconButton,
    InputAdornment,
    Paper,
    CircularProgress
} from "@mui/material";
import {
    Close as CloseIcon,
    Search as SearchIcon,
    Send as SendIcon,
    CheckCircle as CheckCircleIcon
} from "@mui/icons-material";
import { getClients } from "../../services/TraineeService";
import { useCompany } from "../../contexts/CompanyContext";

// Helper to format date
const formatDate = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date.toLocaleDateString('he-IL');
};

// Helper to get status info with date
const getStatusInfo = (status, assignedDate, completedDate) => {
    switch (status) {
        case 'COMPLETED':
            return {
                label: 'הושלם',
                color: '#16A34A',
                bgColor: '#DCFCE7',
                dateLabel: completedDate ? `הושלם ב-${formatDate(completedDate)}` : null
            };
        case 'PARTIAL_ANSWERED':
            return {
                label: 'מילוי חלקי',
                color: '#EA580C',
                bgColor: '#FFEDD5',
                dateLabel: assignedDate ? `נשלח ב-${formatDate(assignedDate)}` : null
            };
        case 'PENDING':
        default:
            return {
                label: 'ממתין',
                color: '#2563EB',
                bgColor: '#DBEAFE',
                dateLabel: assignedDate ? `נשלח ב-${formatDate(assignedDate)}` : null
            };
    }
};

const SendFormDialog = ({ open, onClose, onSend, form, recipientsLoading = false }) => {
    const { company } = useCompany();
    const [selectedTrainees, setSelectedTrainees] = useState([]);
    const [selectedTraineeMap, setSelectedTraineeMap] = useState({}); // Store full trainee objects by id
    const [searchQuery, setSearchQuery] = useState("");
    const [isMandatory, setIsMandatory] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [trainees, setTrainees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formName, setFormName] = useState("");

    // Helper to fetch all trainees (all pages)
    const fetchAllTrainees = async (companyObj, keyword = null) => {
        const PAGE_SIZE = 100;

        // First request to get total count
        const firstResponse = await getClients(0, PAGE_SIZE, keyword, 0, 'ALL', 'ALL', companyObj, null);
        const firstData = firstResponse.data;
        const totalElements = firstData.totalElements || 0;
        const totalPages = Math.ceil(totalElements / PAGE_SIZE);

        let allTrainees = [...(firstData.content || [])];

        // Fetch remaining pages in parallel if there are more
        if (totalPages > 1) {
            const pagePromises = [];
            for (let page = 1; page < totalPages; page++) {
                pagePromises.push(getClients(page, PAGE_SIZE, keyword, 0, 'ALL', 'ALL', companyObj, null));
            }
            const responses = await Promise.all(pagePromises);
            responses.forEach(res => {
                allTrainees = allTrainees.concat(res.data?.content || []);
            });
        }

        return allTrainees;
    };

    // Fetch trainees from server
    const fetchTrainees = useCallback(async (keyword = null) => {
        setLoading(true);
        try {
            const companyObj = form?.companyId ? { id: form.companyId } : company;
            const allTrainees = await fetchAllTrainees(companyObj, keyword);
            const mappedTrainees = allTrainees?.map(trainee => ({
                id: trainee.id,
                name: trainee.name || `${trainee.firstName || ''} ${trainee.lastName || ''}`.trim(),
                email: trainee.email || '',
                enabled: trainee.enabled
            }));
            setTrainees(mappedTrainees);
        } catch (e) {
            console.error('Error fetching trainees:', e);
            setTrainees([]);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company, form?.companyId]);

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (open) {
            setSelectedTrainees([]);
            setSelectedTraineeMap({});
            setSearchQuery("");
            setIsMandatory(false);
            setIsSent(false);
            setFormName(form?.title || "");
            setLoading(true);

            // Fetch all trainees after dialog opens
            const loadTrainees = async () => {
                const companyObj = form?.companyId ? { id: form.companyId } : company;

                try {
                    const allTrainees = await fetchAllTrainees(companyObj, null);
                    const mappedTrainees = allTrainees?.map(trainee => ({
                        id: trainee.id,
                        name: trainee.name || `${trainee.firstName || ''} ${trainee.lastName || ''}`.trim(),
                        email: trainee.email || '',
                        enabled: trainee.enabled
                    }));
                    setTrainees(mappedTrainees);
                } catch (e) {
                    console.error('Error fetching trainees:', e);
                    setTrainees([]);
                } finally {
                    setLoading(false);
                }
            };
            loadTrainees();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Debounced search - fetch from server when search query changes
    useEffect(() => {
        if (!open || !searchQuery) return;

        const timeoutId = setTimeout(() => {
            fetchTrainees(searchQuery);
        }, 300); // 300ms debounce

        return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    // Trainees are now filtered server-side, so just use the fetched list
    const filteredTrainees = trainees;

    // Get already sent recipients with their status info (from form.recipients passed by parent)
    const alreadySentMap = useMemo(() => {
        const map = {};
        (form?.recipients || []).forEach(r => {
            map[r.id] = {
                status: r.status,
                assignedDate: r.assignedDate,
                completedDate: r.completedDate
            };
        });
        return map;
    }, [form?.recipients]);

    // Check if a trainee can be selected (not blocked by pending/partial status)
    const canSelectTrainee = (traineeId) => {
        const sentInfo = alreadySentMap[traineeId];
        if (!sentInfo) return true; // Never sent - can select
        // Block if PENDING or PARTIAL_ANSWERED, allow if COMPLETED
        return sentInfo.status === 'COMPLETED';
    };

    const handleToggleTrainee = (traineeId) => {
        if (!canSelectTrainee(traineeId)) return;

        if (selectedTrainees.includes(traineeId)) {
            setSelectedTrainees(selectedTrainees.filter(id => id !== traineeId));
            setSelectedTraineeMap(prev => {
                const next = { ...prev };
                delete next[traineeId];
                return next;
            });
        } else {
            setSelectedTrainees([...selectedTrainees, traineeId]);
            const trainee = trainees.find(t => t.id === traineeId);
            if (trainee) {
                setSelectedTraineeMap(prev => ({ ...prev, [traineeId]: trainee }));
            }
        }
    };

    const handleSelectAll = () => {
        const availableTrainees = filteredTrainees.filter(t => canSelectTrainee(t.id));
        if (selectedTrainees.length === availableTrainees.length) {
            setSelectedTrainees([]);
            setSelectedTraineeMap({});
        } else {
            setSelectedTrainees(availableTrainees.map(t => t.id));
            const newMap = {};
            availableTrainees.forEach(t => { newMap[t.id] = t; });
            setSelectedTraineeMap(prev => ({ ...prev, ...newMap }));
        }
    };

    const handleSend = () => {
        if (selectedTrainees.length === 0) return;

        const selectedTraineeObjects = Object.values(selectedTraineeMap);
        onSend(selectedTraineeObjects, isMandatory, formName);
        setIsSent(true);

        // Close after showing success
        setTimeout(() => {
            onClose();
        }, 1500);
    };

    const availableTrainees = filteredTrainees.filter(t => canSelectTrainee(t.id));
    const allSelected = availableTrainees.length > 0 && selectedTrainees.length === availableTrainees.length;

    if (!form) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    maxHeight: "90vh",
                    direction: "rtl"
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    padding: "20px 24px",
                    borderBottom: "1px solid #E2E8F0"
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: "20px",
                            color: "#1E293B",
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                            marginBottom: "4px"
                        }}
                    >
                        שליחת טופס למתאמנים
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "14px",
                            color: "#64748B",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}
                    >
                        {form.title}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: "#64748B" }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <DialogContent sx={{ padding: "24px" }}>
                {!isSent ? (
                    <>
                        {/* Search */}
                        <TextField
                            fullWidth
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="חיפוש מתאמן..."
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "#64748B" }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                marginBottom: "16px",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px"
                                }
                            }}
                        />

                        {/* Mandatory Toggle */}
                        <Paper
                            elevation={0}
                            sx={{
                                backgroundColor: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                                borderRadius: "8px",
                                padding: "16px",
                                marginBottom: "16px"
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "14px",
                                            color: "#1E293B",
                                            fontFamily: '"Heebo", "Roboto", sans-serif',
                                            marginBottom: "4px"
                                        }}
                                    >
                                        חובה למילוי מיידי
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            color: "#64748B",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        }}
                                    >
                                        {isMandatory
                                            ? "מתאמנים חייבים למלא את הטופס"
                                            : "מתאמנים יכולים למלא בהמשך"
                                        }
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={isMandatory}
                                    onChange={(e) => setIsMandatory(e.target.checked)}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                            color: "#57837E"
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                            backgroundColor: "#57837E"
                                        }
                                    }}
                                />
                            </Box>
                        </Paper>

                        {/* Form Name */}
                        <TextField
                            fullWidth
                            value={formName}
                            onChange={(e) => setFormName(e.target.value)}
                            label="שם הטופס"
                            placeholder="הזן שם לטופס..."
                            size="small"
                            sx={{
                                marginBottom: "16px",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px"
                                },
                                "& .MuiInputLabel-root": {
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />

                        {(loading || recipientsLoading) ? (
                            <Box sx={{ display: "flex", justifyContent: "center", padding: "48px" }}>
                                <CircularProgress sx={{ color: "#57837E" }} />
                            </Box>
                        ) : (
                            <>
                                {/* Select All */}
                                {availableTrainees.length > 0 && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            backgroundColor: "#E8F5F3",
                                            border: "1px solid #57837E33",
                                            borderRadius: "8px",
                                            padding: "12px 16px",
                                            marginBottom: "12px",
                                            cursor: "pointer",
                                            "&:hover": {
                                                backgroundColor: "#DCF0ED"
                                            }
                                        }}
                                        onClick={handleSelectAll}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={allSelected}
                                                    sx={{
                                                        color: "#57837E",
                                                        "&.Mui-checked": {
                                                            color: "#57837E"
                                                        }
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: "14px",
                                                        color: "#57837E",
                                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                                    }}
                                                >
                                                    בחר את כולם
                                                </Typography>
                                            }
                                            sx={{ margin: 0 }}
                                        />
                                    </Paper>
                                )}

                                {/* Selected Count */}
                                {selectedTrainees.length > 0 && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            backgroundColor: "#57837E1A",
                                            border: "1px solid #57837E33",
                                            borderRadius: "8px",
                                            padding: "12px 16px",
                                            marginBottom: "12px"
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "14px",
                                                color: "#57837E",
                                                fontFamily: '"Heebo", "Roboto", sans-serif'
                                            }}
                                        >
                                            נבחרו {selectedTrainees.length} מתאמנים
                                        </Typography>
                                    </Paper>
                                )}

                                {/* Trainees List */}
                                <Box sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "8px",
                                    maxHeight: "350px",
                                    overflowY: "auto",
                                    paddingRight: "4px",
                                    "&::-webkit-scrollbar": {
                                        width: "6px"
                                    },
                                    "&::-webkit-scrollbar-track": {
                                        backgroundColor: "#F1F5F9",
                                        borderRadius: "3px"
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: "#CBD5E1",
                                        borderRadius: "3px",
                                        "&:hover": {
                                            backgroundColor: "#94A3B8"
                                        }
                                    }
                                }}>
                                    {filteredTrainees.map((trainee) => {
                                        const sentInfo = alreadySentMap[trainee.id];
                                        const isSelected = selectedTrainees.includes(trainee.id);
                                        const statusInfo = sentInfo ? getStatusInfo(sentInfo.status, sentInfo.assignedDate, sentInfo.completedDate) : null;
                                        const isBlocked = sentInfo && (sentInfo.status === 'PENDING' || sentInfo.status === 'PARTIAL_ANSWERED');

                                        return (
                                            <Paper
                                                key={trainee.id}
                                                elevation={0}
                                                onClick={() => handleToggleTrainee(trainee.id)}
                                                sx={{
                                                    border: isSelected
                                                        ? "1px solid #57837E"
                                                        : "1px solid #E2E8F0",
                                                    backgroundColor: isBlocked
                                                        ? "#F1F5F9"
                                                        : isSelected
                                                            ? "#E8F5F3"
                                                            : "#FFFFFF",
                                                    borderRadius: "8px",
                                                    padding: "12px 16px",
                                                    cursor: isBlocked ? "not-allowed" : "pointer",
                                                    opacity: isBlocked ? 0.8 : 1,
                                                    transition: "all 0.2s ease",
                                                    "&:hover": {
                                                        backgroundColor: isBlocked
                                                            ? "#F1F5F9"
                                                            : isSelected
                                                                ? "#DCF0ED"
                                                                : "#F8FAFC"
                                                    }
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        disabled={isBlocked}
                                                        onChange={() => handleToggleTrainee(trainee.id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{
                                                            color: "#57837E",
                                                            "&.Mui-checked": {
                                                                color: "#57837E"
                                                            },
                                                            padding: 0
                                                        }}
                                                    />
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    fontSize: "14px",
                                                                    color: "#1E293B",
                                                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                                                }}
                                                            >
                                                                {trainee.name}
                                                            </Typography>
                                                            <Box
                                                                sx={{
                                                                    backgroundColor: trainee.enabled ? "#D1FAE5" : "#FEE2E2",
                                                                    color: trainee.enabled ? "#10B981" : "#DC2626",
                                                                    padding: "1px 8px",
                                                                    borderRadius: "10px",
                                                                    fontSize: "11px",
                                                                    fontWeight: 600,
                                                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                                                }}
                                                            >
                                                                {trainee.enabled ? "פעיל" : "לא פעיל"}
                                                            </Box>
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "12px",
                                                                color: "#64748B",
                                                                fontFamily: '"Heebo", "Roboto", sans-serif'
                                                            }}
                                                        >
                                                            {trainee.email}
                                                        </Typography>
                                                    </Box>
                                                    {statusInfo && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                alignItems: "flex-end",
                                                                gap: "2px"
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    backgroundColor: statusInfo.bgColor,
                                                                    color: statusInfo.color,
                                                                    padding: "4px 10px",
                                                                    borderRadius: "12px",
                                                                    fontSize: "12px",
                                                                    fontWeight: 600,
                                                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                                                }}
                                                            >
                                                                {statusInfo.label}
                                                            </Box>
                                                            {statusInfo.dateLabel && (
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "10px",
                                                                        color: "#64748B",
                                                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                                                    }}
                                                                >
                                                                    {statusInfo.dateLabel}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    )}
                                                    {isSelected && !isBlocked && (
                                                        <CheckCircleIcon sx={{ color: "#57837E", fontSize: 20 }} />
                                                    )}
                                                </Box>
                                            </Paper>
                                        );
                                    })}

                                    {filteredTrainees.length === 0 && (
                                        <Box sx={{ textAlign: "center", padding: "32px" }}>
                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    color: "#64748B",
                                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                                }}
                                            >
                                                לא נמצאו מתאמנים
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </>
                        )}
                    </>
                ) : (
                    /* Success State */
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "48px 24px"
                        }}
                    >
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                borderRadius: "50%",
                                backgroundColor: "#E8F5F3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginBottom: "16px"
                            }}
                        >
                            <CheckCircleIcon sx={{ fontSize: 32, color: "#57837E" }} />
                        </Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "20px",
                                color: "#1E293B",
                                marginBottom: "8px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            הטופס נשלח בהצלחה!
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            הטופס נשלח ל-{selectedTrainees.length} מתאמנים
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            {/* Footer */}
            {!isSent && (
                <DialogActions
                    sx={{
                        padding: "16px 24px",
                        borderTop: "1px solid #E2E8F0",
                        justifyContent: "space-between"
                    }}
                >
                    <Button
                        onClick={onClose}
                        sx={{
                            color: "#64748B",
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                            fontWeight: 600,
                            borderRadius: "8px",
                            border: "1px solid #E2E8F0",
                            padding: "8px 24px",
                            "&:hover": {
                                backgroundColor: "#F8FAFC",
                                borderColor: "#CBD5E1"
                            }
                        }}
                    >
                        ביטול
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={selectedTrainees.length === 0}
                        startIcon={<SendIcon />}
                        sx={{
                            backgroundColor: "#57837E",
                            color: "#FFFFFF",
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                            fontWeight: 600,
                            borderRadius: "8px",
                            padding: "8px 24px",
                            gap: "8px",
                            "& .MuiButton-startIcon": {
                                marginLeft: "0",
                                marginRight: "0"
                            },
                            "&:hover": {
                                backgroundColor: "#46706B"
                            },
                            "&.Mui-disabled": {
                                backgroundColor: "#CBD5E1",
                                color: "#FFFFFF"
                            }
                        }}
                    >
                        שלח טופס
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default SendFormDialog;
